import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Avatar,
  Alert,
  FormControl,
  Select,
  MenuItem,
  Pagination,
  Skeleton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Search as SearchIcon,
  Favorite as FavoriteIcon,
  Visibility as ViewIcon,
  Schedule as DurationIcon,
  LocationOn as LocationIcon,
  TrendingUp as TrendingIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import UnsplashAttribution from '../components/UnsplashAttribution';
import { useTranslation } from 'react-i18next';
import SEOHead from '../components/SEOHead';
import DocumentMarketingHero from '../components/DocumentMarketingHero';
import api from '../config/api';
import { THEME_COLORS } from '../constants/theme';
import { DOCUMENT, documentPageCanvasSx, documentHeroSearchSx, documentEyebrowSx } from '../constants/documentTheme';
import { partnerOutlineBtnSx, partnerLinkSx } from '../components/PartnerListChrome';

const travelStyleColors = {
  budget: '#4caf50',
  'mid-range': '#2196f3',
  luxury: '#9c27b0',
  backpacker: '#ff9800',
  family: '#e91e63',
  adventure: '#f44336',
  cultural: '#795548',
  relaxation: '#00bcd4',
};

const eventCategoryConfig = {
  sport:           { label: 'Sport',      color: '#e53935', emoji: '🏆' },
  music:           { label: 'Music',      color: '#8e24aa', emoji: '🎵' },
  cultural:        { label: 'Cultural',   color: '#6d4c41', emoji: '🎭' },
  food:            { label: 'Food',       color: '#ef6c00', emoji: '🍴' },
  theme_park:      { label: 'Theme Park', color: '#1e88e5', emoji: '🎢' },
  city_experience: { label: 'City',       color: '#00897b', emoji: '🏙️' },
  layover:         { label: 'Layover',    color: '#546e7a', emoji: '✈️' },
  nature:          { label: 'Nature',     color: '#43a047', emoji: '🌿' },
  festival:        { label: 'Festival',   color: '#d81b60', emoji: '🎉' },
  expo:            { label: 'Expo',       color: '#5c6bc0', emoji: '🎪' },
  parade:          { label: 'Parade',     color: '#f4511e', emoji: '🎊' },
  seasonal:        { label: 'Seasonal',   color: '#26a69a', emoji: '🌸' },
  cruise:          { label: 'Cruise',    color: '#0277bd', emoji: '🚢' },
};

const TravelGuides = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchParams, setSearchParams] = useSearchParams();

  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [destination, setDestination] = useState(searchParams.get('destination') || '');
  const [style, setStyle] = useState(searchParams.get('style') || '');
  const [sort, setSort] = useState(searchParams.get('sort') || 'popular');
  const [page, setPage] = useState(parseInt(searchParams.get('page') || '1'));
  const [pagination, setPagination] = useState(null);
  const [filters, setFilters] = useState({ destinations: [], styles: [] });

  // Event guides state
  const [eventGuides, setEventGuides] = useState([]);
  const [eventGuidesLoading, setEventGuidesLoading] = useState(true);

  // Fetch event guides — re-fetch when search changes
  useEffect(() => {
    const fetchEventGuides = async () => {
      setEventGuidesLoading(true);
      try {
        const params = new URLSearchParams();
        if (search) {
          params.set('search', search);
          params.set('limit', '6');
        } else {
          params.set('featured', 'true');
          params.set('limit', '6');
        }
        const response = await api.get(`/event-guides?${params.toString()}`);
        setEventGuides(response.data.guides || []);
      } catch (err) {
        console.error('Error fetching event guides:', err);
      } finally {
        setEventGuidesLoading(false);
      }
    };
    fetchEventGuides();
  }, [search]);

  const fetchGuides = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (destination) params.set('destination', destination);
      if (style) params.set('style', style);
      params.set('sort', sort);
      params.set('page', page.toString());
      params.set('limit', '12');

      const response = await api.get(`/guides?${params.toString()}`);
      
      if (response.data.success) {
        setGuides(response.data.guides);
        setPagination(response.data.pagination);
        setFilters(response.data.filters || { destinations: [], styles: [] });
      }
    } catch (err) {
      console.error('Error fetching guides:', err);
      setError(t('guides.fetchError'));
    } finally {
      setLoading(false);
    }
  }, [search, destination, style, sort, page, t]);

  useEffect(() => {
    fetchGuides();
  }, [fetchGuides]);

  // Update URL params
  useEffect(() => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (destination) params.set('destination', destination);
    if (style) params.set('style', style);
    if (sort !== 'popular') params.set('sort', sort);
    if (page > 1) params.set('page', page.toString());
    setSearchParams(params, { replace: true });
  }, [search, destination, style, sort, page, setSearchParams]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchGuides();
  };

  const handleClearFilters = () => {
    setSearch('');
    setDestination('');
    setStyle('');
    setSort('popular');
    setPage(1);
  };

  const formatNumber = (num) => {
    if (num >= 1000) return `${(num / 1000).toFixed(1)}k`;
    return num.toString();
  };

  return (
    <Box sx={documentPageCanvasSx}>
      <SEOHead
        title={`${t('guides.pageTitle')} | Déjà Vu Nomad`}
        description={t('guides.pageDescription')}
        canonical="https://dejavunomad.com/guides"
      />

      <DocumentMarketingHero
        eyebrow={t('nav.tabGuides')}
        title={t('guides.heroTitle')}
        subtitle={t('guides.heroSubtitle')}
      >
        <Box component="form" onSubmit={handleSearch} sx={{ maxWidth: 600, mx: 'auto' }}>
          <TextField
            fullWidth
            size="small"
            placeholder={t('guides.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={documentHeroSearchSx}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: DOCUMENT.oxblood }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </DocumentMarketingHero>

      {/* Popular Events & Experiences Section */}
      {(eventGuidesLoading || eventGuides.length > 0) && (
        <Container maxWidth="xl" sx={{ pt: 4, pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography sx={documentEyebrowSx}>
              {search ? 'Event Guides' : 'Popular Events & Experiences'}
            </Typography>
            <Button
              endIcon={<ArrowForwardIcon />}
              onClick={() => navigate('/event-guides')}
              sx={partnerLinkSx}
            >
              View all
            </Button>
          </Box>

          {eventGuidesLoading ? (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Skeleton variant="rounded" height={isMobile ? 220 : 350} sx={{ borderRadius: 3 }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <Grid container spacing={2}>
                  {Array.from({ length: 4 }).map((_, i) => (
                    <Grid item xs={6} key={i}>
                      <Skeleton variant="rounded" height={isMobile ? 140 : 168} sx={{ borderRadius: 3 }} />
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>
          ) : eventGuides.length > 0 && (
            <Grid container spacing={2}>
              {/* First event - large card */}
              <Grid item xs={12} md={6}>
                <Card
                  onClick={() => navigate(`/event-guides/${eventGuides[0].slug}`)}
                  sx={{
                    cursor: 'pointer', borderRadius: 3, overflow: 'hidden',
                    height: '100%', minHeight: { xs: 220, md: 350 },
                    position: 'relative',
                    transition: 'all 0.3s ease',
                    '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 16px 40px rgba(0,0,0,0.15)' },
                    '&:hover .overlay': { background: 'linear-gradient(to bottom, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.3) 50%, transparent 100%)' },
                  }}
                >
                  <CardMedia
                    component="img"
                    image={eventGuides[0].cover_image}
                    alt={eventGuides[0].title}
                    sx={{ height: '100%', minHeight: { xs: 220, md: 350 }, objectFit: 'cover' }}
                  />
                  <Box className="overlay" sx={{
                    position: 'absolute', top: 0, left: 0, right: 0,
                    background: 'linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.15) 60%, transparent 100%)',
                    p: { xs: 2, md: 3 }, color: 'white', transition: 'all 0.3s ease',
                  }}>
                    {/* Tags */}
                    <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                      {eventGuides[0].category && (
                        <Chip
                          label={`${eventCategoryConfig[eventGuides[0].category]?.emoji || ''} ${eventCategoryConfig[eventGuides[0].category]?.label || eventGuides[0].category}`}
                          size="small"
                          sx={{
                            bgcolor: eventCategoryConfig[eventGuides[0].category]?.color || '#607d8b',
                            color: 'white', fontWeight: 700, fontSize: '0.7rem', height: 24,
                          }}
                        />
                      )}
                      <Chip
                        icon={<DurationIcon sx={{ fontSize: 13, color: 'white !important' }} />}
                        label={`${eventGuides[0].recommended_days} days`}
                        size="small"
                        sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontSize: '0.7rem', height: 24 }}
                      />
                    </Box>
                    <Typography variant={isMobile ? 'h6' : 'h4'} fontWeight={800}>{eventGuides[0].title}</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      <LocationIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'text-bottom' }} />
                      {eventGuides[0].location_name}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5, display: { xs: 'none', md: 'block' } }}>
                      {eventGuides[0].subtitle}
                    </Typography>
                  </Box>
                </Card>
              </Grid>

              {/* Remaining events - smaller cards */}
              <Grid item xs={12} md={6}>
                <Grid container spacing={2}>
                  {eventGuides.slice(1, 5).map(event => {
                    const catCfg = eventCategoryConfig[event.category] || { label: event.category, color: '#607d8b', emoji: '📌' };
                    return (
                      <Grid item xs={6} key={event.id}>
                        <Card
                          onClick={() => navigate(`/event-guides/${event.slug}`)}
                          sx={{
                            cursor: 'pointer', borderRadius: 3, overflow: 'hidden',
                            position: 'relative', height: { xs: 140, md: 168 },
                            transition: 'all 0.3s ease',
                            '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 12px 32px rgba(0,0,0,0.12)' },
                          }}
                        >
                          <CardMedia
                            component="img"
                            image={event.cover_image}
                            alt={event.title}
                            sx={{ height: '100%', objectFit: 'cover' }}
                          />
                          <Box sx={{
                            position: 'absolute', top: 0, left: 0, right: 0,
                            background: 'linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, transparent 100%)',
                            p: 1.5, color: 'white',
                          }}>
                            <Chip
                              label={`${catCfg.emoji} ${catCfg.label}`}
                              size="small"
                              sx={{
                                bgcolor: catCfg.color, color: 'white',
                                fontWeight: 700, fontSize: '0.6rem', height: 22, mb: 0.5,
                              }}
                            />
                            <Typography variant="subtitle2" fontWeight={700} sx={{ lineHeight: 1.2 }}>{event.title}</Typography>
                            <Typography variant="caption" sx={{ opacity: 0.85, display: 'flex', alignItems: 'center', gap: 0.3 }}>
                              <LocationIcon sx={{ fontSize: 11 }} />
                              {event.location_name}
                            </Typography>
                          </Box>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              </Grid>
            </Grid>
          )}

        </Container>
      )}

      {/* Filters & Content */}
      <Container maxWidth="xl" sx={{ py: 3 }}>
        {/* Community Travel Guides */}
        <Typography sx={{ ...documentEyebrowSx, mb: 2 }}>
          Community Travel Guides
        </Typography>

        {/* Filter Bar */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2, 
          mb: 3, 
          flexWrap: 'wrap'
        }}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <Select
              value={destination}
              onChange={(e) => { setDestination(e.target.value); setPage(1); }}
              displayEmpty
              sx={{ borderRadius: 2, fontSize: '0.875rem' }}
            >
              <MenuItem value="">{t('guides.allDestinations')}</MenuItem>
              {filters.destinations.map(d => (
                <MenuItem key={d} value={d}>{d}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <Select
              value={style}
              onChange={(e) => { setStyle(e.target.value); setPage(1); }}
              displayEmpty
              sx={{ borderRadius: 2, fontSize: '0.875rem' }}
            >
              <MenuItem value="">{t('guides.allStyles')}</MenuItem>
              {filters.styles.map(s => (
                <MenuItem key={s} value={s}>
                  {t(`guides.style.${s}`, s.charAt(0).toUpperCase() + s.slice(1))}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 130 }}>
            <Select
              value={sort}
              onChange={(e) => { setSort(e.target.value); setPage(1); }}
              sx={{ borderRadius: 2, fontSize: '0.875rem' }}
            >
              <MenuItem value="popular">{t('guides.sortPopular')}</MenuItem>
              <MenuItem value="newest">{t('guides.sortNewest')}</MenuItem>
              <MenuItem value="most-liked">{t('guides.sortMostLiked')}</MenuItem>
              <MenuItem value="most-viewed">{t('guides.sortMostViewed')}</MenuItem>
            </Select>
          </FormControl>

          {(search || destination || style) && (
            <Button 
              size="small" 
              onClick={handleClearFilters}
              sx={partnerLinkSx}
            >
              {t('guides.clearFilters')}
            </Button>
          )}

          {pagination && (
            <Typography variant="body2" sx={{ ml: 'auto', color: 'text.secondary' }}>
              {t('guides.showingResults', { 
                count: pagination.total,
                showing: guides.length
              })}
            </Typography>
          )}
        </Box>

        {/* Error */}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Loading Skeletons */}
        {loading && (
          <Grid container spacing={3}>
            {[...Array(6)].map((_, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 3 }} />
                <Skeleton width="80%" sx={{ mt: 1 }} />
                <Skeleton width="60%" />
              </Grid>
            ))}
          </Grid>
        )}

        {/* Guide Cards */}
        {!loading && guides.length > 0 && (
          <Grid container spacing={3}>
            {guides.map(guide => (
              <Grid item xs={12} sm={6} md={4} key={guide.id}>
                <Card
                  onClick={() => navigate(`/guides/${guide.slug}`)}
                  sx={{
                    cursor: 'pointer',
                    borderRadius: 3,
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 12px 32px rgba(0,0,0,0.12)',
                    }
                  }}
                >
                  {/* Cover Image */}
                  <Box sx={{ position: 'relative', height: 180 }}>
                    {guide.cover_image ? (
                      <CardMedia
                        component="img"
                        height="180"
                        image={guide.cover_image}
                        alt={guide.title}
                        sx={{ objectFit: 'cover' }}
                      />
                    ) : (
                      <Box sx={{ 
                        height: 180, 
                        background: `linear-gradient(135deg, ${THEME_COLORS.primary.main} 0%, ${THEME_COLORS.secondary.main} 100%)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <LocationIcon sx={{ fontSize: 48, color: 'rgba(255,255,255,0.3)' }} />
                      </Box>
                    )}

                    {/* Unsplash Attribution */}
                    <UnsplashAttribution
                      imageUrl={guide.cover_image}
                      photographerName={guide.unsplash_photographer_name}
                      photographerUsername={guide.unsplash_photographer_username}
                      variant="overlay"
                    />

                    {/* Duration Badge */}
                    <Chip
                      icon={<DurationIcon sx={{ fontSize: 14, color: 'white !important' }} />}
                      label={`${guide.duration_days} ${guide.duration_days === 1 ? t('guides.day') : t('guides.days')}`}
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 8,
                        left: 8,
                        bgcolor: 'rgba(0,0,0,0.7)',
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '0.7rem',
                      }}
                    />

                    {/* Travel Style Badge */}
                    {guide.travel_style && (
                      <Chip
                        label={t(`guides.style.${guide.travel_style}`, guide.travel_style)}
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          bgcolor: travelStyleColors[guide.travel_style] || '#666',
                          color: 'white',
                          fontWeight: 600,
                          fontSize: '0.65rem',
                        }}
                      />
                    )}

                    {/* Featured Badge */}
                    {!!guide.is_featured && (
                      <Chip
                        icon={<TrendingIcon sx={{ fontSize: 14, color: '#fff !important' }} />}
                        label={t('guides.featured')}
                        size="small"
                        sx={{
                          position: 'absolute',
                          bottom: 8,
                          left: 8,
                          bgcolor: '#ff6d00',
                          color: 'white',
                          fontWeight: 700,
                          fontSize: '0.65rem',
                        }}
                      />
                    )}
                  </Box>

                  <CardContent sx={{ p: 2 }}>
                    {/* Title */}
                    <Typography variant="h6" sx={{ 
                      fontWeight: 700, 
                      mb: 0.5,
                      fontSize: '1rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {guide.title}
                    </Typography>

                    {/* Destination */}
                    {guide.destination && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                        <LocationIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                          {guide.destination}
                        </Typography>
                      </Box>
                    )}

                    {/* Description */}
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      sx={{ 
                        mb: 1.5,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        fontSize: '0.8rem',
                        lineHeight: 1.4
                      }}
                    >
                      {guide.description || t('guides.noDescription')}
                    </Typography>

                    {/* Author & Stats */}
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar 
                          src={guide.author?.avatar}
                          sx={{ width: 24, height: 24, fontSize: '0.7rem', bgcolor: THEME_COLORS.secondary.main }}
                        >
                          {guide.author?.name?.charAt(0) || '?'}
                        </Avatar>
                        <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                          {guide.author?.name}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                          <ViewIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                          <Typography variant="caption" color="text.secondary">
                            {formatNumber(guide.view_count)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                          <FavoriteIcon sx={{ fontSize: 14, color: guide.user_liked ? 'error.main' : 'text.disabled' }} />
                          <Typography variant="caption" color="text.secondary">
                            {formatNumber(guide.like_count)}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Empty State */}
        {!loading && guides.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <LocationIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              {t('guides.noGuidesFound')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('guides.tryDifferentSearch')}
            </Typography>
            <Button 
              variant="outlined" 
              onClick={handleClearFilters}
              sx={partnerOutlineBtnSx}
            >
              {t('guides.clearFilters')}
            </Button>
          </Box>
        )}

        {/* Pagination */}
        {pagination && pagination.pages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Pagination
              count={pagination.pages}
              page={page}
              onChange={(_, value) => setPage(value)}
              color="primary"
              shape="rounded"
            />
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default TravelGuides;
